import { useSelector } from "react-redux";

/**
 * IMATS equivalent of PCI's "sticky fields" business logic
 * (see legacy BookingsInterface.vbs/AddStickyCharge and
 * ContainerCommon.vbs/HandleGetDefaultsFromStickyFields).
 *
 * Config is stored in the same ConfigStickyFields table PCI uses
 * (AppContext / Field_Source / Field_Name), configured via
 * Config > Sticky Fields By AppContext. It is loaded once at app
 * startup into state.config.stickyFields (see App.jsx).
 *
 * A sticky field entry means: "when a new record is created for this
 * AppContext, carry this field's value forward from the record the
 * user was just working with, instead of leaving it blank."
 */

/**
 * Get the list of field names configured as sticky for a given AppContext.
 * @param {string} appContext - e.g. "Charges"
 * @returns {string[]} field names (Field_Name column), e.g. ["chargeDescriptionID", "courtNameID"]
 */
export const useStickyFieldNames = (appContext) => {
  return useSelector((state) =>
    (state.config.stickyFields || [])
      .filter((f) => f?.appContext === appContext)
      .map((f) => f?.fieldName)
      .filter(Boolean)
  );
};

// ConfigStickyFields.Field_Name is data migrated straight out of PCI, where
// it always held the raw SQL column name (e.g. "Court_Number",
// "Controlling_Agency_ORI_ID"). This app's JSON uses camelCase property
// names instead (e.g. "courtNumber", "controllingAgencyORIID"). Stripping
// everything but letters/digits and lowercasing puts both spellings of the
// same field on equal footing:
//   "Court_Name_ID"             -> "courtnameid"
//   "courtNameID"               -> "courtnameid"
//   "Controlling_Agency_ORI_ID" -> "controllingagencyoriid"
//   "controllingAgencyORIID"    -> "controllingagencyoriid"
// Exported so callers can check whether a specific field is configured
// sticky without needing their own copy of this normalization (see
// ChargesList/index.jsx's handling of "Inmate TrackingID", which needs
// special-case treatment beyond the flat copy pickStickyFields does).
export const normalizeKey = (key) => String(key).replace(/[^a-zA-Z0-9]/g, "").toLowerCase();

/**
 * Copy only the configured sticky fields from a source record onto a new
 * record, mirroring PCI's AddStickyCharge: everything else is left out so
 * the new record starts blank except for the fields marked sticky.
 *
 * Field_Name is matched against the source record's keys case/separator-
 * insensitively (see normalizeKey above), since the config may be authored
 * with either PCI's DB column names or this app's camelCase field names.
 * A sticky field that has no counterpart on the source (e.g. it lives on a
 * nested child object rather than directly on this record, or was
 * mistyped) is silently skipped rather than throwing.
 *
 * @param {object} source       - the previously selected/entered record
 * @param {string[]} fieldNames - result of useStickyFieldNames()
 * @returns {object} a partial record containing only the sticky field values,
 *                    keyed by the source's own (camelCase) field names
 */
export const pickStickyFields = (source, fieldNames) => {
  if (!source || !fieldNames?.length) return {};

  const sourceKeyByNormalizedName = {};
  Object.keys(source).forEach((key) => {
    sourceKeyByNormalizedName[normalizeKey(key)] = key;
  });

  const result = {};
  fieldNames.forEach((name) => {
    const sourceKey = sourceKeyByNormalizedName[normalizeKey(name)];
    if (sourceKey) {
      result[sourceKey] = source[sourceKey];
    }
  });
  return result;
};
